<div class="hidden-general-dialog settings-shell-dialog">
  <!-- Header -->
  <div
    class="flex items-start sm:items-center justify-between !px-4 !py-3 sm:!py-2 mb-2 border-b border-slate-100 gap-3">
    <h2 mat-dialog-title class="!m-0 !p-0 text-[18px] font-bold text-slate-800 leading-tight flex-1">
      <ng-container [ngSwitch]="step">
        <span *ngSwitchCase="'confirm'">Update {{ isGlobalMode() ? 'Global' : 'Personalized' }} Settings</span>
        <span *ngSwitchCase="'processing'">Updating settings…</span>
        <span *ngSwitchDefault>Settings Updated</span>
      </ng-container>
    </h2>
    <button mat-icon-button class="text-slate-400 hover:text-slate-600 transition-colors shrink-0 -mt-1 sm:mt-0"
      (click)="onCancel()" aria-label="Close dialog" [disabled]="step === 'processing'">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <mat-dialog-content class="dialog-content">
    <ng-container *ngIf="step === 'confirm'">
      <!-- Information -->
      <div class="flex-col p-4 rounded-xl mb-5 mt-2 bg-[#fffbeb] border border-solid border-[#fde68a] shadow-sm">
        <div class="flex gap-3 items-start w-full">
          <mat-icon class="text-[#d97706] shrink-0 mt-0.5">info</mat-icon>
          <div class="flex flex-col gap-1.5">
            <p class="text-[#92400e] text-[14px] font-medium !mb-0 !mt-0 leading-snug tracking-[inherit]">
              <ng-container *ngIf="isGlobalMode()">
                Update your global workspace settings to align with your new plan limits. Restricted features will be disabled automatically.
              </ng-container>
              <ng-container *ngIf="!isGlobalMode()">
                Update the settings for the employees below to align with your new plan limits. Restricted features will be disabled automatically.
              </ng-container>
            </p>
          </div>
        </div>
      </div>

      <!-- Affected employees -->
      <div class="flex flex-col gap-2 mb-5" *ngIf="!isGlobalMode() && data.employees?.length">
        <div class="flex items-center justify-between !mb-2 !mt-0">
          <p class="text-[13.5px] font-medium text-slate-600 !m-0">Affected employees ({{ employeeCount }})</p>
          <button (click)="toggleSelectAll()" type="button"
            class="text-[13px] font-medium text-[#1c1c1c] bg-transparent border-none cursor-pointer hover:underline !p-0">
            {{ allSelected ? 'Deselect All' : 'Select All' }}
          </button>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 overflow-y-auto max-h-[180px] pr-1">
          <div
            class="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-solid transition-all cursor-pointer select-none"
            [ngClass]="isEmployeeSelected(emp.id) ? 'border-[#1c1c1c] bg-[rgba(28,28,28,0.06)]' : 'border-[#e5e7eb] bg-[#f6f7f9] hover:border-gray-300'"
            *ngFor="let emp of data.employees" [matTooltip]="getEmployeeTooltip(emp)" matTooltipPosition="above"
            (click)="toggleEmployeeSelection(emp.id)">

            <div class="flex-shrink-0 flex items-center justify-center rounded-full overflow-hidden w-[32px] h-[32px]">
              <ngx-avatars name="{{ emp.name }}" size="32" initialsSize="2"></ngx-avatars>
            </div>
            <p class="!text-[13.5px] font-medium text-slate-700 truncate m-0 flex-1">{{ emp.name }}</p>

            <div class="w-5 h-5 rounded-full border border-solid flex items-center justify-center transition-colors"
              [ngClass]="isEmployeeSelected(emp.id) ? 'bg-[#1c1c1c] border-[#1c1c1c]' : 'border-gray-300 bg-white'">
              <mat-icon *ngIf="isEmployeeSelected(emp.id)"
                class="text-white !w-3 !h-3 !text-[12px] flex items-center justify-center">check</mat-icon>
            </div>
          </div>
        </div>
      </div>

      <!-- Settings Form -->
      <form [formGroup]="settingsForm" class="flex flex-col gap-4 mb-3">
      <ng-container *ngIf="!data.isAdminControlOnly">
        <!-- Interval Settings -->
        <div class="form-section w-full border border-[#e5e7eb] border-solid !px-[14px] !py-[12px] rounded-lg bg-white">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div class="flex flex-col items-start justify-center">
              <div class="flex items-center gap-1.5 justify-start">
                <mat-icon class="text-slate-500 !text-[18px] !w-[18px] !h-[18px]">timer</mat-icon>
                <p class="m-0 text-[14.5px] font-medium text-slate-800">Periodic Screenshots Interval</p>
              </div>
              <p class="text-slate-500 text-[13px] !m-0 !mt-0">Take screenshots every x minutes</p>
            </div>
            <div class="mode-toggle">
              <app-minute-stepper formControlName="ss_screen_capture_time" [min]="minScreenshotInterval" [max]="60"></app-minute-stepper>
            </div>
          </div>
        </div>

        <!-- Advanced Screenshot Modes -->
        <div class="form-section w-full border border-[#e5e7eb] border-solid !px-[14px] !py-[12px] rounded-lg bg-white">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3" [class.opacity-70]="!advancedTrackingAllowed">
            <div class="flex flex-col items-start justify-center">
              <div class="flex items-center gap-1.5 justify-start">
                <mat-icon class="text-slate-500 !text-[18px] !w-[18px] !h-[18px]">photo_camera</mat-icon>
                <p class="m-0 text-[14.5px] font-medium text-slate-800">Switched App Screenshots</p>
              </div>
              <p *ngIf="!advancedTrackingAllowed" class="text-amber-600 text-[12px] font-medium !m-0 !mt-0 flex items-center gap-1">
                Not supported on your downgraded plan
              </p>
            </div>
            <div class="mode-toggle">
              <mat-slide-toggle color="primary" formControlName="ss_appchange"></mat-slide-toggle>
            </div>
          </div>
        </div>

        <!-- Tracking Controls -->
        <div class="form-section w-full border border-[#e5e7eb] border-solid !px-[14px] !py-[12px] rounded-lg bg-white">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3" [class.opacity-70]="!advancedTrackingAllowed">
            <div class="flex flex-col items-start justify-center">
              <div class="flex items-center gap-1.5 justify-start">
                <mat-icon class="text-slate-500 !text-[18px] !w-[18px] !h-[18px]">more_time</mat-icon>
                <p class="m-0 text-[14.5px] font-medium text-slate-800">Strict Time Tracking</p>
              </div>
              <p *ngIf="!advancedTrackingAllowed" class="text-amber-600 text-[12px] font-medium !m-0 !mt-0 flex items-center gap-1">
                Not supported on your downgraded plan
              </p>
            </div>
            <div class="mode-toggle">
              <mat-slide-toggle color="primary" formControlName="ss_strict_time_tracking"></mat-slide-toggle>
            </div>
          </div>
        </div>

        <!-- Hidden Mode -->
        <div class="form-section w-full border border-[#e5e7eb] border-solid !px-[14px] !py-[12px] rounded-lg bg-white">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3" [class.opacity-70]="!hiddenModeAllowed">
            <div class="flex flex-col items-start justify-center">
              <div class="flex items-center gap-1.5 justify-start">
                <mat-icon class="text-slate-500 !text-[18px] !w-[18px] !h-[18px]">visibility_off</mat-icon>
                <p class="m-0 text-[14.5px] font-medium text-slate-800">Hidden Mode</p>
              </div>
              <p *ngIf="!hiddenModeAllowed" class="text-amber-600 text-[12px] font-medium !m-0 !mt-0 flex items-center gap-1">
                Not supported on your downgraded plan
              </p>
            </div>
            <div class="mode-toggle">
              <mat-slide-toggle color="primary" formControlName="ss_silent"></mat-slide-toggle>
            </div>
          </div>
        </div>
      </ng-container>

      <ng-container *ngIf="data.isAdminControlOnly">
        <!-- Admin Control / Auto Tracking -->
        <div class="form-section w-full border border-[#e5e7eb] border-solid !px-[14px] !py-[12px] rounded-lg bg-white">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3" [class.opacity-70]="!advancedTrackingAllowed">
            <div class="flex flex-col items-start justify-center">
              <div class="flex items-center gap-1.5 justify-start">
                <mat-icon class="text-slate-500 !text-[18px] !w-[18px] !h-[18px]">admin_panel_settings</mat-icon>
                <p class="m-0 text-[14.5px] font-medium text-slate-800">Auto Tracking (Restricted Mode)</p>
              </div>
              <p *ngIf="!advancedTrackingAllowed" class="text-amber-600 text-[12px] font-medium !m-0 !mt-0 flex items-center gap-1">
                Not supported on your downgraded plan
              </p>
            </div>
            <div class="mode-toggle">
              <mat-slide-toggle color="primary" formControlName="ss_automatic_tracking"></mat-slide-toggle>
            </div>
          </div>
        </div>
      </ng-container>
      </form>

      <p class="text-[13px] text-red-500 font-medium text-center mt-2" *ngIf="errorMessage">{{ errorMessage }}</p>
    </ng-container>

    <ng-container *ngIf="step === 'processing'">
      <div class="flex flex-col items-center justify-center py-10 gap-4">
        <mat-spinner diameter="42"></mat-spinner>
        <p class="text-[15px] font-medium text-slate-700 m-0">
          <ng-container *ngIf="isGlobalMode()">Updating global settings…</ng-container>
          <ng-container *ngIf="!isGlobalMode()">Updating settings for {{ selectedCount }} employee{{ selectedCount === 1 ? '' : 's' }}…</ng-container>
        </p>
      </div>
    </ng-container>

    <ng-container *ngIf="step === 'result'">
      <div class="flex-col p-4 rounded-xl mb-4 mt-2 bg-[#ecfdf5] border border-solid border-[#a7f3d0] shadow-sm">
        <div class="flex gap-3 items-center w-full">
          <mat-icon class="text-[#10b981] shrink-0 mt-0.5">check_circle</mat-icon>
          <div class="flex flex-col gap-1.5">
            <p class="text-[#065f46] text-[14px] font-medium !mb-0 !mt-0 leading-snug tracking-[inherit]">
              <ng-container *ngIf="isGlobalMode()">Successfully updated global workspace settings.</ng-container>
              <ng-container *ngIf="!isGlobalMode()">Successfully updated personalized settings for {{ selectedCount }} employee{{ selectedCount === 1 ? '' : 's' }}.</ng-container>
            </p>
          </div>
        </div>
      </div>

      <ng-container *ngIf="!isGlobalMode()">
        <p class="text-[13.5px] font-medium text-slate-600 !m-0 !mb-2">Updated Employees</p>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 overflow-y-auto max-h-[180px] pr-1">
        <div class="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-solid border-[#e5e7eb] bg-[#f6f7f9]"
          *ngFor="let emp of selectedEmployeesList" [matTooltip]="getEmployeeTooltip(emp)" matTooltipPosition="above">
          <div class="flex-shrink-0 flex items-center justify-center rounded-full overflow-hidden w-[32px] h-[32px]">
            <ngx-avatars name="{{ emp.name }}" size="32" initialsSize="2"></ngx-avatars>
          </div>
          <p class="!text-[13.5px] font-medium text-slate-700 truncate m-0 flex-1">{{ emp.name }}</p>

          <div
            class="w-5 h-5 rounded-full border border-solid flex items-center justify-center transition-colors bg-[#10b981] border-[#10b981]">
            <mat-icon class="text-white !w-3 !h-3 !text-[12px] flex items-center justify-center">check</mat-icon>
          </div>
        </div>
      </div>
      </ng-container>
    </ng-container>
  </mat-dialog-content>

  <mat-dialog-actions
    class="flex flex-col-reverse sm:flex-row w-full justify-center sm:!justify-end gap-3 pt-4 pb-2 border-t border-slate-100 mt-2 !mb-0 px-4">
    <ng-container [ngSwitch]="step">
      <ng-container *ngSwitchCase="'confirm'">
        <button mat-stroked-button type="button" class="action-secondary w-full sm:w-auto min-h-[40px] !m-0"
          (click)="onCancel()">Cancel</button>
        <button mat-raised-button color="primary" type="button"
          class="action-primary w-full sm:w-auto min-h-[40px] !m-0"
          [disabled]="(!isGlobalMode() && selectedCount === 0) || settingsForm.invalid" (click)="onConfirm()">
          Update Settings
        </button>
      </ng-container>
      <ng-container *ngSwitchCase="'result'">
        <button mat-raised-button color="primary" type="button"
          class="action-primary w-full sm:w-auto min-h-[40px] !m-0" (click)="onDone()">Done</button>
      </ng-container>
    </ng-container>
  </mat-dialog-actions>
</div>